import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputBase,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface PageToolbarProps {
  tabs?: React.ReactNode;
  actions?: React.ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  hideFilter?: boolean;
  filterContent?: React.ReactNode;
  filterChips?: React.ReactNode;
  isFilterActive?: boolean;
  'data-testid'?: string;
}

export const PageToolbar: React.FC<PageToolbarProps> = ({
  tabs,
  actions,
  searchValue,
  onSearchChange,
  hideFilter = false,
  filterContent,
  filterChips,
  isFilterActive,
  'data-testid': testId,
}) => {
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (hideFilter) setFilterOpen(false);
  }, [hideFilter]);

  const hasSearch = onSearchChange !== undefined;
  const isActive = (hasSearch && !!searchValue) || (isFilterActive ?? false);

  const handleFilterClick = () => {
    if (hasSearch || filterContent) {
      setDraft(searchValue ?? '');
      setFilterOpen(true);
    }
  };

  const handleApply = () => {
    onSearchChange?.(draft);
    setFilterOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  const handleClose = () => {
    setFilterOpen(false);
  };

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      data-testid={testId}
    >
      {/* Main toolbar row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          minHeight: 36,
          py: 0.5,
          px: { xs: 2, sm: 3 },
        }}
      >
        {tabs && <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>{tabs}</Box>}
        {!hideFilter && (
          <Tooltip title="Filter">
            <Button
              ref={filterButtonRef}
              variant="text"
              size="small"
              startIcon={
                <FilterListIcon sx={{ fontSize: '0.875rem !important' }} />
              }
              onClick={handleFilterClick}
              aria-label="Filter"
              sx={{
                transition: 'color 0.2s ease',
                color: isActive ? 'primary.main' : 'text.secondary',
                minWidth: { xs: 'auto', sm: undefined },
                px: { xs: 0.75, sm: undefined },
                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: 'none', sm: 'inline' } }}
              >
                Filter
              </Box>
            </Button>
          </Tooltip>
        )}
        {actions}
      </Box>

      {/* Filter popover — rendered outside toolbar flex row so it doesn't affect layout */}
      {(hasSearch || filterContent) && (
        <Popover
          open={filterOpen}
          anchorEl={filterButtonRef.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: { mt: 1, px: 1.5, py: 1, width: 300, borderRadius: 2 },
            },
          }}
        >
          {hasSearch && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon
                sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }}
              />
              <InputBase
                autoFocus
                placeholder="Search…"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{ flex: 1, typography: 'body2' }}
                data-testid="filter-search-input"
              />
              {draft ? (
                <IconButton
                  size="small"
                  onClick={() => setDraft('')}
                  sx={{ p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              ) : (
                <Box sx={{ width: 30 }} />
              )}
            </Box>
          )}
          {filterContent && (
            <Box
              sx={{
                mt: hasSearch ? 1 : 0,
                borderTop: hasSearch ? '1px solid' : 'none',
                borderColor: 'divider',
                pt: hasSearch ? 1 : 0,
              }}
            >
              {filterContent}
            </Box>
          )}
          {hasSearch && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 0.5,
                mt: 1,
              }}
            >
              <Button
                size="small"
                variant="text"
                onClick={handleApply}
                disabled={!draft.trim()}
                sx={{
                  color: draft.trim() ? 'primary.main' : 'text.secondary',
                }}
              >
                Apply
              </Button>
            </Box>
          )}
        </Popover>
      )}

      {/* Active filters row */}
      {isActive && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            px: { xs: 2, sm: 3 },
            py: 0.75,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {hasSearch && !!searchValue && (
            <Chip
              size="small"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SearchIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    Search
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', mx: 0.25 }}
                  >
                    is
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {searchValue}
                  </Typography>
                </Box>
              }
              onDelete={() => onSearchChange?.('')}
              deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
              sx={{ height: 26, borderRadius: 1 }}
            />
          )}
          {filterChips}
          {hasSearch && !!searchValue && (
            <Button
              size="small"
              variant="text"
              onClick={() => onSearchChange?.('')}
              sx={{
                typography: 'caption',
                color: 'text.secondary',
                ml: 'auto',
                minWidth: 'auto',
              }}
            >
              Clear
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
